import { useContext, useState, Fragment, useMemo } from "react";
import { Menu, MenuItem, Divider, ListSubheader } from "@mui/material";
import { ModuleContext } from "../../context/ModuleContext";
import { UserModuleContext } from "../../context/UserModuleContext";
import { useOktaAuth } from "@okta/okta-react";

import { ActivityLog } from "../../operations/ActivityLog";
import { ResetPassword } from "../../operations/okta/ResetPassword";
import { ModifySecondaryEmail } from "../../operations/okta/ModifySecondaryEmail";
import { AccountStatus } from "../../operations/okta/AccountStatus";
import { RemoveMFAFactor } from "../../operations/okta/RemoveMFAFactor";
import { NidRename } from "../../operations/okta/NidRename";
import { Reinvite } from "../../operations/okta/Reinvite";
import { OktaLog } from "../../operations/OktaLog";
import { PhishedNid } from "../../operations/okta/PhishedNid";
import { SpecialHideFromGal } from "../../operations/okta/SpecialHideFromGal";
import { UnlockAccount } from "../../operations/okta/UnlockAccount";

import { ADAccountStatus } from "../../operations/active_directory/ADAccountStatus";
import { AddProxyAddress } from "../../operations/active_directory/AddProxyAddress";
import { ResetADPassword } from "../../operations/active_directory/ResetADPassword";
import { UpdateEmailForwarding } from "../../operations/active_directory/UpdateEmailForwarding";
import { ExpireADAccount } from "../../operations/active_directory/ExpireADAccount";

import { UnblockMailbox } from "../../operations/office365/UnblockMailbox";
import { Groups } from "../../operations/Groups";
import { O365InfoTable } from "../office365/O365InfoTable";

const WSU_CRIMSON = "#A60F2D";

const pillSx = {
  backgroundColor: WSU_CRIMSON,
  color: "#ffffff",
  borderRadius: "999px",
  mx: 1,
  mb: 0.75,
  px: 2,
  py: 0.5,
  fontSize: "0.8rem",
  fontFamily: "'Montserrat', Arial, sans-serif",
  fontWeight: 500,
  minHeight: 32,
  "&:hover": {
    backgroundColor: "#8a0c25",
  },
};

const sectionSx = {
  fontFamily: "'Montserrat', Arial, sans-serif",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "#4D4D4D",
  lineHeight: "28px",
  mt: 0.5,
};

export const OptionsMenu = ({ username, identifier, isRestricted }) => {
  const { setModule } = useContext(ModuleContext);
  const { userModule } = useContext(UserModuleContext);
  const { authState } = useOktaAuth();

  const [anchorEl, setAnchorEl] = useState(null);

  const openMenu = (event) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const launch = (element, isLog = false, userIdOverride = null) => {
    closeMenu();
    setModule((prev) => ({
      ...prev,
      isVisible: true,
      userId: userIdOverride ?? username,
      element,
      isLog,
    }));
  };

  const menuItems = useMemo(() => {
    const items = [];
    const claims = authState.accessToken.claims;
    const isTier2 = claims.itsTier2 || claims.distributedItTier2;
    const isTier3 = claims.itsTier3 || claims.distributedItTier3;

    const section = (label) => items.push({ type: "section", label });
    const item = (label, component, isLog = false, id = null) =>
        items.push({ type: "item", label, component, isLog, id });

    section("General");
    item("Tech tools action history", <ActivityLog userId={identifier} />, true);
    item("List groups for NID/FID", <Groups />, true);
    if (!isRestricted && claims.itsTier3) {
      item("Disable phished NID", <PhishedNid userId={username} />);
    }

    section("Okta");
    item("Get Okta user logs", <OktaLog userId={identifier} />, true, identifier);
    item("Resend Okta invitation", <Reinvite userId={identifier} />, false, identifier);
    if (!isRestricted && (isTier2 || isTier3)) {
      item("Reset password", <ResetPassword userId={identifier} />, false, identifier);
      item("Add/edit secondary email", <ModifySecondaryEmail userId={identifier} />, false, identifier);
      item("Set special hide from GAL", <SpecialHideFromGal userId={identifier} />, false, identifier);
    }
    if (!isRestricted && claims.itsTier3) {
      item("Rename NID/FID", <NidRename userId={identifier} />, false, identifier);
    }
    if (!isRestricted && userModule.userData["Okta Status"] === "LOCKED_OUT" && (isTier2 || isTier3)) {
      item("Unlock Okta account", <UnlockAccount />);
    }
    if (!isRestricted && isTier3) {
      item("Suspend/unsuspend account", <AccountStatus identifier={identifier} />);
      item("Get/reset Okta factors", <RemoveMFAFactor identifier={identifier} />);
    }

    section("Active Directory");
    if (!isRestricted && (isTier2 || isTier3)) {
      item("Reset AD password", <ResetADPassword />);
      item("Update email forwarding", <UpdateEmailForwarding />);
    }
    if (!isRestricted && isTier3) {
      item("Expire/unexpire AD account", <ExpireADAccount />);
      item("Enable/disable AD", <ADAccountStatus />);
      item("Add new proxy address", <AddProxyAddress />);
    }

    section("Office 365");
    item("List O365 info", <O365InfoTable />);
    if (!isRestricted && claims.itsTier3) {
      item("Unblock Office 365 mailbox", <UnblockMailbox />);
    }

    return items;
  }, [username, identifier, isRestricted, userModule, authState]);

  return (
      <Fragment>
        <div style={{ marginTop: 20 }}>
          {menuItems.map((entry, i) =>
              entry.type === "section" ? (
                  <Fragment key={`section-${entry.label}`}>
                    {i !== 0 && (
                        <Divider sx={{ mx: 1, my: 0.5 }} />
                    )}
                    <ListSubheader disableSticky sx={sectionSx}>
                      {entry.label}
                    </ListSubheader>
                  </Fragment>
              ) : (
                  <MenuItem
                      key={entry.label}
                      onClick={() => launch(entry.component, entry.isLog, entry.id)}
                      sx={pillSx}
                  >
                    {entry.label}
                  </MenuItem>
              )
          )}
        </div>
      </Fragment>
  );
};